// NEW_FILE_JUSTIFIED: SourceCardGrid is a Step 1 sub-component extracted to keep CreateTypeStep1Page under 200 lines; no existing component covers this clickable source-card grid pattern
import React from 'react';
import { FileText, Layers, Copy as CopyIcon, Sparkles } from 'lucide-react';
import type {
  TInsuranceOrgTypeListItem,
  TStartingSource,
  ISourceCardGridProps,
} from '@/@types/insuranceBuilder';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const SOURCE_CARDS: ReadonlyArray<{
  key: TStartingSource;
  label: string;
  desc: string;
  icon: React.ElementType;
}> = [
  { key: 'blank', label: 'Blank', desc: 'Start from scratch', icon: FileText },
  { key: 'predefined', label: 'Predefined', desc: 'Default fields for this type', icon: Layers },
  { key: 'copy', label: 'Copy existing', desc: 'Duplicate from another type', icon: CopyIcon },
  {
    key: 'ai',
    label: 'AI Extract',
    desc: 'Upload a PDF or image of an existing policy',
    icon: Sparkles,
  },
];

export function SourceCardGrid({
  source,
  predefinedEnabled,
  orgTypes,
  copyFromId,
  onSelect,
  onCopyFromId,
}: ISourceCardGridProps) {
  return (
    <div className="space-y-3">
      <p id="source-group-label" className="sr-only">
        Starting fields source
      </p>
      <div
        role="group"
        aria-labelledby="source-group-label"
        className="grid grid-cols-2 gap-3 md:grid-cols-4"
      >
        {SOURCE_CARDS.map(({ key, label, desc, icon: Icon }) => {
          const disabled = key === 'predefined' && !predefinedEnabled;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={source === key}
              disabled={disabled}
              onClick={() => onSelect(key)}
              className={cn(
                'rounded-xl border bg-card p-4 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                source === key
                  ? 'border-primary ring-1 ring-primary'
                  : 'border-border hover:bg-muted',
                disabled && 'opacity-40 cursor-not-allowed hover:bg-card'
              )}
            >
              <Icon
                className={cn(
                  'h-5 w-5 mb-2',
                  source === key ? 'text-primary' : 'text-muted-foreground'
                )}
                aria-hidden
              />
              <p className="text-sm font-semibold">{label}</p>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </button>
          );
        })}
      </div>
      {source === 'predefined' && !predefinedEnabled && (
        <p className="text-xs text-muted-foreground">
          Enter a matching type name to enable Predefined.
        </p>
      )}
      {source === 'copy' && (
        <Select value={copyFromId ?? ''} onValueChange={v => onCopyFromId(v)}>
          <SelectTrigger>
            <SelectValue placeholder="Select a type to copy" />
          </SelectTrigger>
          <SelectContent>
            {(orgTypes ?? []).map((t: TInsuranceOrgTypeListItem) => (
              <SelectItem key={t._id} value={t._id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
